import { CrmNoteType } from '@plato/data/prisma';
import { DialogStateManager } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { formatShortDate } from '@plato/ui-legacy/lib/date-helpers';
import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import {
    Card,
    CardContent,
    CardDetail,
    CardFront,
    CardHeader,
    CardMainAction,
    CardStart,
    CardSubtitle,
    CardTitle,
} from '@plato/ui/containers/card';
import type { TFunction } from 'i18next';
import { CalendarXIcon, NotebookPenIcon, NotepadTextIcon, PaperclipIcon, PhoneOffIcon } from 'lucide-react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import type { CrmNoteDTO } from '../../../server/dto/crm-note-dto.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { EditNoteDialog } from '../../notes/components/edit-note-dialog.tsx';
import { deriveTaskText } from '../../tasks/lib/derive-task-text.ts';

/** A visit or phone call that never happened is labelled as not reached. */
function getNoteHeading(
    t: TFunction<'activity-feed'>,
    note: CrmNoteDTO,
): { icon: ReactElement; label: string } {
    const notReached = note.interactionHappened === false;
    switch (note.noteType) {
        case CrmNoteType.VISIT:
            return notReached
                ? { icon: <CalendarXIcon />, label: t('notes__type__visit_not_reached', { ns: 'notes' }) }
                : { icon: <NotebookPenIcon />, label: t('notes__type__visit', { ns: 'notes' }) };
        case CrmNoteType.PHONE_CALL:
            return notReached
                ? { icon: <PhoneOffIcon />, label: t('notes__type__phone_call_not_reached', { ns: 'notes' }) }
                : { icon: <NotebookPenIcon />, label: t('notes__type__phone_call', { ns: 'notes' }) };
        case CrmNoteType.NOTE:
            return { icon: <NotepadTextIcon />, label: t('notes__type__note', { ns: 'notes' }) };
    }
}

export type ActivityFeedNoteItemProps = {
    note: CrmNoteDTO;
};

/**
 * One logged interaction with a two-line preview of its text. Opens the note dialog.
 *
 * Takes the note itself rather than a feed record, so the pinned block — which reads
 * `getPinnedNotes` and never sees a feed record — renders the same card.
 */
export function ActivityFeedNoteItem({ note }: ActivityFeedNoteItemProps): ReactElement {
    const { t } = useTranslation('activity-feed');
    const locale = useResolvedLocale();
    const sendEvent = useAnalytics();

    const { ref: inViewRef } = useInView({
        threshold: 0,
        triggerOnce: true,
        onChange: (inView) => {
            if (inView) {
                sendEvent('customer_interaction_viewed', {
                    note_id: note.noteId,
                    customer_id: note.customerId,
                    note_type: note.noteType,
                });
            }
        },
    });

    const showNote = (): void => {
        void DialogStateManager.show(EditNoteDialog, { noteId: note.noteId });
    };

    const { icon, label: typeLabel } = getNoteHeading(t, note);

    const occurredAt = formatShortDate(note.occurredAt, locale);
    const attachmentCount = note.attachments.length;
    const { title, detail } = deriveTaskText(note.titleRich);
    const text = detail ? `${title} · ${detail}` : title;

    return (
        <Card ref={inViewRef}>
            <CardFront>
                <CardStart render={icon} />
                <CardContent>
                    <CardHeader>
                        <CardTitle className="truncate">{typeLabel}</CardTitle>
                        <CardSubtitle className="tabular-nums">
                            <span className="truncate">{note.createdBy.fullName}</span> · {occurredAt}
                            {attachmentCount > 0 && (
                                <>
                                    {' · '}
                                    <span className="flex items-center gap-tight">
                                        <PaperclipIcon className="size-3 shrink-0" />
                                        {t('card__attachments', { count: attachmentCount })}
                                    </span>
                                </>
                            )}
                        </CardSubtitle>
                    </CardHeader>
                    {text && <CardDetail className="line-clamp-2">{text}</CardDetail>}
                </CardContent>
                <CardMainAction aria-label={t('card__open', { name: typeLabel })} onClick={showNote} />
            </CardFront>
        </Card>
    );
}
